// SDK › Signals' rules (D114, APS-428): a trigger the policy defines (spec 15.8), edited on its own
// page as the mock's two nodes, a trigger and a keep. The trigger is `on` with up to 8 conditions
// that must all hold; the keep is the families that list the rule as `rule:<id>`, with the window
// and `after` its entry carries (15.4). A rule is in use while a family lists it. The page edits a
// draft of the rule and writes it into the Signals draft, which publishes the app's policy whole.
import type {
  InstrumentationRule as Rule,
  RuleCondition,
  RuleOp,
  RuleScalar,
  SignalFamily,
} from '../api/types'
import { type After, type Draft, FAMILIES, LIMITS } from './signals'

/** Spec 15.8's bounds. */
export const MAX_RULES = 50
export const MAX_CONDITIONS = 8
export const MAX_IN_VALUES = 50
export const MAX_GLOB = 128
export const RULE_ID = /^[a-z0-9._-]{1,64}$/

/** What a rule reads (`on`): every log record, one event's, every span, one span name's, requests. */
export const ON_KINDS = ['network', 'log', 'log_event', 'span', 'span_name'] as const
export type OnKind = (typeof ON_KINDS)[number]

/** The operators in the inspector's order, as the mock words them. */
export const OPS = [
  'eq',
  'in',
  'prefix',
  'gte',
  'lte',
  'status_class',
  'glob',
  'exists',
] as const satisfies readonly RuleOp[]

export const STATUS_CLASSES = ['1xx', '2xx', '3xx', '4xx', '5xx'] as const

/**
 * Attributes whose values are numbers, so `is` and `is one of` compare numbers there (the spec
 * never coerces a type: a status code sent as a number never equals the text "503").
 */
const NUMERIC_ATTRS = new Set([
  'http.response.status_code',
  'duration_ms',
  'severity',
  'app.screen.load.ttfd',
  'apsio.ui.tap.count',
  'apsio.device.font_scale',
])

/** One condition as the inspector edits it: the value as typed. */
export interface ConditionDraft {
  attr: string
  op: RuleOp
  value: string
}

/** The families a rule keeps, and how: its trigger entry in each of them. */
export interface RuleDraft {
  /** Empty for a new rule until it is published; then the slug of its name. */
  id: string
  name: string
  onKind: OnKind
  onName: string
  where: ConditionDraft[]
  families: SignalFamily[]
  windowMinutes: number
  after: After
}

export const emptyRule = (): RuleDraft => ({
  id: '',
  name: '',
  onKind: 'network',
  onName: '',
  where: [{ attr: '', op: 'eq', value: '' }],
  families: [],
  windowMinutes: LIMITS.windowMinutes.default,
  after: 'session',
})

const ON_PREFIX: Partial<Record<OnKind, string>> = { log_event: 'log:', span_name: 'span:' }

/** The policy's `on` for a draft's kind and name. */
export function onOf(kind: OnKind, name: string): string {
  const prefix = ON_PREFIX[kind]
  if (prefix) return `${prefix}${name.trim()}`
  return kind === 'network' ? 'network' : kind === 'log' ? 'log' : 'span'
}

function onKindOf(on: string): { onKind: OnKind; onName: string } {
  if (on.startsWith('log:')) return { onKind: 'log_event', onName: on.slice(4) }
  if (on.startsWith('span:')) return { onKind: 'span_name', onName: on.slice(5) }
  if (on === 'log' || on === 'span' || on === 'network') return { onKind: on, onName: '' }
  return { onKind: 'log_event', onName: on }
}

function valueText(c: RuleCondition): string {
  if (Array.isArray(c.value)) return c.value.map(String).join(', ')
  return String(c.value)
}

/** One typed value as the spec compares it: a number on numeric attributes, `true`/`false` as such. */
function scalar(attr: string, text: string): RuleScalar {
  const t = text.trim()
  if (t === 'true' || t === 'false') return t === 'true'
  if (NUMERIC_ATTRS.has(attr) && /^-?\d+(\.\d+)?$/.test(t)) return Number(t)
  return t
}

/** A condition's value as the policy takes it, or undefined when the text does not fit the op. */
export function conditionValue(c: ConditionDraft): RuleScalar | RuleScalar[] | undefined {
  const t = c.value.trim()
  switch (c.op) {
    case 'exists':
      return t !== 'false'
    case 'gte':
    case 'lte':
      return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : undefined
    case 'status_class':
      return (STATUS_CLASSES as readonly string[]).includes(t) ? t : undefined
    case 'glob':
      return t.length > 0 && Array.from(t).length <= MAX_GLOB ? t : undefined
    case 'in': {
      const xs = t
        .split(',')
        .map((x) => x.trim())
        .filter((x) => x.length > 0)
      return xs.length > 0 && xs.length <= MAX_IN_VALUES
        ? xs.map((x) => scalar(c.attr, x))
        : undefined
    }
    default:
      return t.length > 0 ? scalar(c.attr, t) : undefined
  }
}

/** A rule of the policy as the page edits it, with the families that keep it. */
export function toRuleDraft(rule: Rule, draft: Draft): RuleDraft {
  const trigger = `rule:${rule.id}`
  const families = FAMILIES.filter((f) => draft.families[f].triggers.includes(trigger))
  const own = families.map((f) => draft.families[f].overrides[trigger]).find((o) => o !== undefined)
  return {
    id: rule.id,
    name: rule.name,
    ...onKindOf(rule.on),
    where: (rule.where ?? []).map((c) => ({ attr: c.attr, op: c.op, value: valueText(c) })),
    families,
    windowMinutes:
      own?.windowMinutes ??
      (families[0] ? draft.families[families[0]].windowMinutes : LIMITS.windowMinutes.default),
    after: own?.after ?? 'session',
  }
}

/** A name's id: lowercase, words joined by `_`, unique among the app's rules. */
export function ruleIdFor(name: string, taken: readonly string[]): string {
  const base =
    name
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 56) || 'rule'
  let id = base
  for (let n = 2; taken.includes(id); n++) id = `${base}_${n}`
  return id
}

/**
 * What is wrong with a rule before it is published: the fields, in the page's order. `families`
 * is no error: a rule no family lists is kept for later and fires nowhere.
 */
export function ruleErrors(
  r: RuleDraft,
  others: readonly Rule[],
): ('name' | 'onName' | 'conditions' | 'window' | 'limit' | `condition:${number}`)[] {
  const errors: ReturnType<typeof ruleErrors> = []
  if (r.name.trim().length === 0 || r.name.trim().length > 80) errors.push('name')
  if ((r.onKind === 'log_event' || r.onKind === 'span_name') && r.onName.trim().length === 0)
    errors.push('onName')
  if (r.where.length > MAX_CONDITIONS) errors.push('conditions')
  r.where.forEach((c, i) => {
    if (c.attr.trim().length === 0 || conditionValue(c) === undefined) errors.push(`condition:${i}`)
  })
  const w = r.windowMinutes
  if (!Number.isInteger(w) || w < LIMITS.windowMinutes.min || w > LIMITS.windowMinutes.max)
    errors.push('window')
  if (r.id === '' && others.length >= MAX_RULES) errors.push('limit')
  return errors
}

/** The rule as the policy holds it (`name` and the times stay on the server). */
export function toRule(r: RuleDraft, id: string, held?: Rule): Rule {
  return {
    id,
    name: r.name.trim(),
    on: onOf(r.onKind, r.onName),
    where: r.where.map((c) => ({
      attr: c.attr.trim(),
      op: c.op,
      value: conditionValue(c) as RuleScalar | RuleScalar[],
    })),
    updated_at: held?.updated_at ?? null,
    updated_by: held?.updated_by ?? null,
  }
}

/**
 * The Signals draft with this rule in it: the rule replaced or added, and its trigger listed by
 * exactly the families it keeps, each with the rule's window and `after`.
 */
export function withRule(draft: Draft, r: RuleDraft): Draft {
  const id =
    r.id ||
    ruleIdFor(
      r.name,
      draft.rules.map((x) => x.id),
    )
  const trigger = `rule:${id}`
  const held = draft.rules.find((x) => x.id === id)
  const rule = toRule(r, id, held)
  const rules = held ? draft.rules.map((x) => (x.id === id ? rule : x)) : [...draft.rules, rule]
  const families = { ...draft.families }
  for (const f of FAMILIES) {
    const fam = families[f]
    const keeps = r.families.includes(f)
    const triggers = fam.triggers.filter((x) => x !== trigger)
    const { [trigger]: _, ...overrides } = fam.overrides
    families[f] = keeps
      ? {
          ...fam,
          triggers: [...triggers, trigger],
          overrides: {
            ...overrides,
            [trigger]: {
              ...(r.windowMinutes !== fam.windowMinutes ? { windowMinutes: r.windowMinutes } : {}),
              ...(r.after === 'window' ? { after: 'window' as const } : {}),
            },
          },
        }
      : { ...fam, triggers, overrides }
  }
  return { ...draft, rules, families }
}

/** The Signals draft without the rule: gone from the policy and from every family's triggers. */
export function withoutRule(draft: Draft, id: string): Draft {
  const trigger = `rule:${id}`
  const families = { ...draft.families }
  for (const f of FAMILIES) {
    const { [trigger]: _, ...overrides } = families[f].overrides
    families[f] = {
      ...families[f],
      triggers: families[f].triggers.filter((x) => x !== trigger),
      overrides,
    }
  }
  return { ...draft, rules: draft.rules.filter((x) => x.id !== id), families }
}

/** Whether a family's mode lets a trigger release it (always sends already; never ignores triggers). */
export const familyTakesTriggers = (draft: Draft, f: SignalFamily): boolean =>
  draft.families[f].mode === 'on_trigger' || draft.families[f].mode === 'sampled'

/** The rules no family lists: in the policy, firing nowhere. */
export const unusedRules = (draft: Draft): string[] =>
  draft.rules
    .map((r) => r.id)
    .filter((id) => !FAMILIES.some((f) => draft.families[f].triggers.includes(`rule:${id}`)))

/** The one-step templates (Mosaic's mapping): a trigger with AND conditions and a keep. */
export const TEMPLATES = [
  {
    id: 'force_quit',
    onKind: 'log_event',
    onName: 'apsio.session.summary',
    where: [{ attr: 'apsio.exit.reason', op: 'eq', value: 'user_quit' }],
    families: ['logs', 'spans'],
  },
  {
    id: 'endpoint_5xx',
    onKind: 'network',
    onName: '',
    where: [
      { attr: 'url.template', op: 'eq', value: '/checkout' },
      { attr: 'http.response.status_code', op: 'status_class', value: '5xx' },
    ],
    families: ['network', 'logs'],
  },
  {
    id: 'slow_screen',
    onKind: 'span_name',
    onName: 'app.screen.load',
    where: [{ attr: 'duration_ms', op: 'gte', value: '1500' }],
    families: ['spans', 'logs'],
  },
  {
    id: 'declined',
    onKind: 'log',
    onName: '',
    where: [{ attr: 'body', op: 'glob', value: '*payment declined*' }],
    families: ['logs', 'network'],
  },
  {
    id: 'rage_taps',
    onKind: 'log_event',
    onName: 'apsio.ui.rage_tap',
    where: [{ attr: 'app.screen.name', op: 'eq', value: 'Checkout' }],
    families: ['replay', 'logs'],
  },
  {
    id: 'offline',
    onKind: 'log_event',
    onName: 'apsio.device.connectivity',
    where: [{ attr: 'network.connection.type', op: 'eq', value: 'unavailable' }],
    families: ['logs', 'network'],
  },
] as const satisfies readonly {
  id: string
  onKind: OnKind
  onName: string
  where: readonly ConditionDraft[]
  families: readonly SignalFamily[]
}[]
export type TemplateId = (typeof TEMPLATES)[number]['id']

/** A new rule started from a template; its name is the template's, worded by the page. */
export function fromTemplate(id: TemplateId, name: string): RuleDraft {
  const t = TEMPLATES.find((x) => x.id === id)
  if (!t) return emptyRule()
  return {
    ...emptyRule(),
    name,
    onKind: t.onKind,
    onName: t.onName,
    where: t.where.map((c) => ({ ...c })),
    families: [...t.families],
  }
}

/** A rule's trigger name for a family's triggers. */
export const ruleTrigger = (id: string) => `rule:${id}`
